import {
  ButtonGroupRadio,
  CategoryNameSchemasType,
  CloneTableRowButton,
  CreateTableRowButton,
  DataRowType,
  DeleteTableRowButton,
  DraftableRow,
  emitFocusFormInput,
  ErrorCell,
  ErrorHeader,
  ErrorRowIndicatorCell,
  FormField,
  FormTableReadonlySequenceNumberField,
  getFormTableFieldName,
  GroupDescriptionType,
  InputDataType,
  Padding,
  PopconfirmDelete,
  RequiredText,
  SampleCommentType,
  TableStore,
  TanstackFormTable,
  Tooltip,
  useEditSessionContext,
  useEditSessionFormRowsContext,
  useInsertFormRow,
} from '@atlas-frontend/sdk';
import {
  createContext,
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  Table as TableInstance,
} from '@tanstack/react-table';
import { Badge, Button, Checkbox, Flex } from 'antd';
import { makeInputDataFilterFn } from '../utils';
import { adjustColumns, COLUMN_SEQUENCE_NO, COLUMN_SUBJECT } from './utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  useInputDataDetailsStoreValue,
  useSetInputDataDetailsStoreValue,
} from '../details/InputDataDetailsContext';
import { InputDataCell } from './InputDataCell';
import { InputDataEditCellProps } from './types';
import { InputDataEditable } from './InputDataEditable';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import { hasInvalidFilter, toggleInvalidFilter } from './filterInputs';
import translations from './InputDataEditTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { AddCommentModal } from './AddCommentModal';
import { TableFilterSortProps } from './InputDataViewTable';
import { colorYellow } from '@atlas-frontend/design-tokens';
import { useCalculatePreview } from './useCalculatePreview';

const { addTranslations, t } = i18nNsFactory('input-data-edit-table');
addTranslations(translations);

const columnHelper = createColumnHelper<DataRowType>();

export interface InputDataEditTableProps {
  data?: InputDataType;
  date?: string;
  descriptionId?: number;
  groupId?: number;
  isLoading?: boolean;
  isDetailed?: boolean;
  subject?: string;
  comments: SampleCommentType[];
  descriptions: GroupDescriptionType[];
  sampleId?: number;
  onAddComment: (comment: SampleCommentType) => void;
  tableProps?: TableFilterSortProps;
}

export function InputDataEditTable({
  data,
  date,
  isDetailed,
  subject,
  comments,
  descriptionId,
  descriptions,
  sampleId,
  isLoading,
  onAddComment,
  tableProps,
}: InputDataEditTableProps) {
  const formRowCount: number = useWatch({ name: 'rows.length' }) ?? 0;
  const [mutationErrors] = useInputDataDetailsStoreValue('mutationErrors');
  const tableRef = useRef<TableInstance<DataRowType>>(null);

  const errorRowCount = mutationErrors?.errors.size;

  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const setTotal = useSetInputDataDetailsStoreValue('total');
  const [addCommentModalOpen, setAddCommentModalOpen] = useState(false);

  const { getValues } = useFormContext();
  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) =>
      setSelected(
        new Set(e.target.checked ? (getValues('rows') ?? []).keys() : []),
      ),
    [getValues],
  );

  const onCheckChange = useCallback(
    (checked: boolean, index: number) => {
      setSelected((current) => {
        const newSelected = new Set(current);
        if (checked) {
          newSelected.add(index);
        } else {
          newSelected.delete(index);
        }
        return newSelected;
      });
    },
    [setSelected],
  );

  const filters = tableProps?.filters;
  const [showOnlyErrorRows] = useMemo(
    () => hasInvalidFilter(filters ?? []),
    [filters],
  );
  // Selection holds row indexes, so it is cleared whenever other rows are shown.
  // Compared by content, as filter changes can produce new arrays with the same content.
  const [searchParams] = useSearchParams();
  const shownRowsKey = JSON.stringify([
    filters,
    tableProps?.sort,
    searchParams.get('pageNumber'),
    searchParams.get('pageSize'),
  ]);
  useEffect(() => {
    setSelected(new Set());
  }, [shownRowsKey]);

  const { insertRow } = useEditSessionContext();
  const clearSelected = useCallback(() => setSelected(new Set()), []);
  const { calculatePreview, isCalculating } =
    useCalculatePreview(clearSelected);

  const onFilter = tableProps?.onFilter;
  const toggleInvalid = useCallback(
    () => onFilter?.(toggleInvalidFilter),
    [onFilter],
  );

  useEffect(() => setTotal(formRowCount), [formRowCount, setTotal]);
  const {
    setUnvalidatedRowIndex,
    removeRow,
    preparePendingRow,
    isPreparingPendingRow,
    goToLastPage,
    getRowOffset,
  } = useEditSessionFormRowsContext();
  const insertFormRow = useInsertFormRow('rows', true);

  const adjustedColumns = useMemo(
    () => (data ? adjustColumns(data, isDetailed) : []),
    [data, isDetailed],
  );
  const { focusField, hasSequenceNumberColumn } = useMemo(
    () => ({
      focusField: adjustedColumns.find(
        (col) => col.code !== COLUMN_SEQUENCE_NO && col.code !== COLUMN_SUBJECT,
      )?.code,
      hasSequenceNumberColumn: adjustedColumns.some(
        (col) => col.code === COLUMN_SEQUENCE_NO,
      ),
    }),
    [adjustedColumns],
  );

  const sequenceNumber = (index: number) =>
    hasSequenceNumberColumn ? getRowOffset() + index + 1 : undefined;

  const handleAddRow = async () => {
    if ((await preparePendingRow()) === 'blocked') {
      return;
    }
    await goToLastPage();
    await new Promise((resolve) => setTimeout(resolve));

    const index = getValues('rows').length;
    insertFormRow(index, {
      isNew: true,
      [COLUMN_SUBJECT]: subject,
      [COLUMN_SEQUENCE_NO]: sequenceNumber(index),
    });
    setUnvalidatedRowIndex(index);
    if (focusField) {
      setTimeout(() => emitFocusFormInput(`rows.${index}.${focusField}`));
    }
  };

  const cloneRow = async (index: number) => {
    if ((await preparePendingRow()) === 'blocked') {
      return;
    }
    const source = getValues(`rows.${index}`);
    await goToLastPage();
    const clone = {
      ...source,
      id: null,
      [COLUMN_SEQUENCE_NO]: sequenceNumber(getValues('rows').length),
    };
    const id = await insertRow(clone);
    // Rows may have been added while inserting, so append at the current end.
    const appendIndex = getValues('rows').length;
    insertFormRow(appendIndex, { ...clone, id });
    return appendIndex;
  };

  const { columns } = useMemo(() => {
    if (!data) {
      return {
        columns: [] as ColumnDef<DataRowType, any>[],
        rows: [] as DataRowType[],
      };
    }
    const { labels, rows } = data;

    const columnDefinitions: ColumnDef<DataRowType, any>[] = adjustedColumns
      .map((column) => {
        if (column.hidden) {
          return null;
        }
        if (column.code === COLUMN_SEQUENCE_NO) {
          return columnHelper.accessor(COLUMN_SEQUENCE_NO, {
            header: column.name,
            cell: FormTableReadonlySequenceNumberField,
            meta: {
              width: getWidth(COLUMN_SEQUENCE_NO),
              className: 'sticky-left',
              textAlign: 'right',
            },
            enableColumnFilter: false,
            enableSorting: false,
          });
        }

        return columnHelper.accessor(column.code, {
          header: () => (
            <ErrorHeader columnCode={column.code}>
              <RequiredText required={column.required}>
                <Tooltip title={`${column.code} - ${column.name}`}>
                  {column.code}
                </Tooltip>
              </RequiredText>
            </ErrorHeader>
          ),
          cell: (props) => (
            <InputDataFormCell
              {...props}
              columnType={column}
              date={date}
              labels={labels}
            />
          ),
          meta: {
            width: getWidth(column.code),
            textAlign: column.type === 'number' ? 'right' : 'left',
          },
          filterFn: makeInputDataFilterFn(labels?.[column.name]),
          sortingFn: 'alphanumeric',
        });
      })
      .filter((col) => !!col) as ColumnDef<DataRowType, any>[];

    columnDefinitions.unshift(
      columnHelper.display({
        id: 'selected',
        header: SelectAllCheckbox,
        cell: SelectedCell,
        meta: { width: 32, className: 'selected sticky-left' },
        enableColumnFilter: false,
        enableSorting: false,
      }),
    );

    columnDefinitions.push(
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <AddRowButton />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <CloneRowButton context={props} />
            <DeleteTableRowButton
              id={`delete-table-row-${props.row.index}`}
              confirm={{
                onConfirm: () => {
                  removeRow(
                    props.row.original as DraftableRow,
                    props.row.index,
                  );
                },
              }}
            />
          </Flex>
        ),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    );

    return { columns: columnDefinitions, rows };
  }, [data, date, adjustedColumns, removeRow]);

  // Rows read the context, so keep its value stable: only selection changes re-render them.
  const handlersRef = useRef({ handleAddRow, cloneRow });
  useEffect(() => {
    handlersRef.current = { handleAddRow, cloneRow };
  });
  const [isAddingRow, setIsAddingRow] = useState(false);
  const runAddingRow = useCallback(async <T,>(add: () => Promise<T>) => {
    setIsAddingRow(true);
    try {
      return await add();
    } finally {
      setIsAddingRow(false);
    }
  }, []);
  const onAddRow = useCallback(
    () => runAddingRow(() => handlersRef.current.handleAddRow()),
    [runAddingRow],
  );
  const onCloneRow = useCallback(
    (index: number) => runAddingRow(() => handlersRef.current.cloneRow(index)),
    [runAddingRow],
  );
  const tableContextValue = useMemo(
    () => ({ selected, onCheckAllChange, onCheckChange, onAddRow, onCloneRow }),
    [selected, onCheckAllChange, onCheckChange, onAddRow, onCloneRow],
  );

  return (
    <>
      <Padding vertical={10}>
        <Flex gap="small">
          <ButtonGroupRadio
            value={showOnlyErrorRows}
            onChange={toggleInvalid}
            options={[
              { value: false, label: t('button.showAllRows.label') },
              {
                value: true,
                label: (
                  <Flex align="center" gap="small">
                    {t('button.showErrorRows.label')}
                    <Badge count={errorRowCount} showZero={false} />
                  </Flex>
                ),
              },
            ]}
          />
          <StyledButton
            id="add-comment"
            icon={<Icon.Plus size={18} />}
            size="middle"
            onClick={() => setAddCommentModalOpen(true)}
            disabled={!sampleId}
          >
            {t('button.addComment.label')}
          </StyledButton>
          <CalculateButton
            id="calculate"
            size="middle"
            loading={isCalculating}
            onClick={() => calculatePreview()}
            disabled={!sampleId || isCalculating || isLoading}
          >
            {t('button.calculate.label')}
          </CalculateButton>
          {selected.size > 0 && (
            <DeleteSelectedRowsButton
              selected={selected}
              setSelected={setSelected}
            />
          )}
        </Flex>
      </Padding>
      <InputDataEditTableContext.Provider value={tableContextValue}>
        <IsAddingRowContext.Provider
          value={isAddingRow || isPreparingPendingRow}
        >
          <TanstackFormTable
            ref={tableRef}
            name="rows"
            columns={columns}
            isLoading={isLoading}
            scrollContainerSelector="#detail-layout-main-content"
            sticky={true}
            virtual
            estimateRowSize={estimateEditRowSize}
            prettyDisplayErrors
            manualFiltering
            manualSorting
            {...tableProps}
          />
        </IsAddingRowContext.Provider>
      </InputDataEditTableContext.Provider>
      {addCommentModalOpen && sampleId && (
        <AddCommentModal
          comments={comments}
          descriptionId={descriptionId}
          descriptions={descriptions}
          sampleId={sampleId}
          onClose={() => setAddCommentModalOpen(false)}
          onSubmit={onAddComment}
        />
      )}
    </>
  );
}

const DeleteSelectedRowsButton = ({
  selected,
  setSelected,
}: {
  selected: Set<number>;
  setSelected: Dispatch<SetStateAction<Set<number>>>;
}) => {
  const { removeRows } = useEditSessionFormRowsContext();
  const formRows = useWatch({ name: 'rows', defaultValue: [] as any[] });

  const onDeleteSelected = () => {
    removeRows?.(Array.from(selected).map((index) => [formRows[index], index]));
    setSelected(new Set());
  };

  return (
    <PopconfirmDelete
      onDelete={onDeleteSelected}
      genitiveModelName={t('button.deleteSelected.genitive')}
    >
      <Button id="delete-selected">{t('button.deleteSelected.label')}</Button>
    </PopconfirmDelete>
  );
};

interface InputDataEditTableContextValue {
  selected: Set<number>;
  onCheckAllChange: (e: CheckboxChangeEvent) => void;
  onCheckChange: (checked: boolean, index: number) => void;
  onAddRow: () => Promise<void>;
  onCloneRow: (index: number) => Promise<number | undefined>;
}

const InputDataEditTableContext = createContext<InputDataEditTableContextValue>(
  undefined!,
);
const IsAddingRowContext = createContext(false);

const SelectAllCheckbox = () => {
  const { selected, onCheckAllChange } = useContext(InputDataEditTableContext);
  const formRowCount: number = useWatch({ name: 'rows.length' }) ?? 0;
  return (
    <Checkbox
      id="check-all-rows"
      indeterminate={selected.size > 0 && selected.size < formRowCount}
      checked={selected.size > 0 && selected.size === formRowCount}
      onChange={onCheckAllChange}
    />
  );
};

const AddRowButton = () => {
  const { onAddRow } = useContext(InputDataEditTableContext);
  const isAddingRow = useContext(IsAddingRowContext);

  return (
    <CreateTableRowButton
      id="create-table-row"
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => void onAddRow()}
      disabled={isAddingRow}
    />
  );
};

const CloneRowButton = ({ context }: { context: CellContext<any, any> }) => {
  const { onCloneRow } = useContext(InputDataEditTableContext);
  const isAddingRow = useContext(IsAddingRowContext);
  // Getter instead of a subscription, so the button doesn't re-render with the table.
  const getVirtualizer = TableStore.useGetValue('virtualizer');

  const handleClick = async () => {
    const cloneIndex = await onCloneRow(context.row.index);
    if (cloneIndex !== undefined) {
      setTimeout(() =>
        getVirtualizer()?.scrollToIndex(cloneIndex, { align: 'end' }),
      );
    }
  };

  return (
    <CloneTableRowButton
      context={context}
      onMouseDown={(event) => event.preventDefault()}
      onClick={handleClick}
      disabled={isAddingRow}
    />
  );
};

const SelectedCell: FC<CellContext<any, any>> = (props) => {
  const { selected, onCheckChange } = useContext(InputDataEditTableContext);
  return (
    <ErrorRowIndicatorCell rowIndex={props.row.index}>
      <Padding left={4}>
        <Checkbox
          id={'check-' + props.row.index}
          checked={selected.has(props.row.index)}
          onChange={(e) => onCheckChange(e.target.checked, props.row.index)}
        />
      </Padding>
    </ErrorRowIndicatorCell>
  );
};

const InputDataFormCell: FC<InputDataEditCellProps> = memo((props) => {
  const cellName = getFormTableFieldName(props);

  const labels = useMemo(() => {
    let labels: CategoryNameSchemasType | undefined;
    if (props.columnType.id) {
      labels = props.labels?.[props.columnType.id];
    }
    if (!labels && props.columnType.classifierCode) {
      labels = props.labels?.[props.columnType.classifierCode];
      if (props.columnType.allCategories && labels) {
        labels = Object.fromEntries(
          Object.entries(labels).filter(
            ([, value]) => value.categoryType === 'category',
          ),
        );
      }
    }
    if (!labels) {
      labels = props.labels?.[props.columnType.code];
    }
    return labels;
  }, [
    props.labels,
    props.columnType.allCategories,
    props.columnType.code,
    props.columnType.classifierCode,
    props.columnType.id,
  ]);

  if (props.columnType.dataSubjectAttribute) {
    return props.renderValue();
  }

  return (
    <ErrorCell
      rowIndex={Number(props.row.original.id) || 0}
      columnCode={props.columnType.code}
    >
      <FormField
        name={cellName}
        size="small"
        preview={{
          render: (value) => (
            <InputDataCell
              code={props.columnType.code}
              dataType={props.columnType.dataType}
              labels={props.labels}
              value={value}
            />
          ),
        }}
      >
        <InputDataEditable
          {...props}
          categoryNames={labels}
          cellName={cellName}
        />
      </FormField>
    </ErrorCell>
  );
});

// Inputs make edit rows 33px tall instead of the default 31px. A smaller estimate would leave the last rows unreachable by scrolling.
const estimateEditRowSize = () => 33;

const getWidth = (code: string) => {
  switch (code) {
    case 'sequenceNumber':
      return 110;
    case 'SUBJEKT':
      return 200;
    default:
      return undefined;
  }
};

const StyledButton = styled(Button)`
  .ant-btn-icon {
    height: 18px;
  }
`;

const CalculateButton = styled(Button)`
  background-color: ${colorYellow[1]};
  .ant-btn-icon {
    height: 18px;
  }
`;
